import { Box, Typography, IconButton, Chip } from '@mui/material';
import { CaretDown, CheckCircle } from '@phosphor-icons/react';
import { useState } from 'react';
import { useUser } from '../../../hooks/useUser';

interface ModifierItem {
  title: string;
  price: number;
  in_stock: boolean;
  food_type: string;
  ref_id: string;
}

interface ModifierGroup {
  title: string;
  ref_id: string;
  type: 'variant' | 'addon';
  min_selection: number;
  max_selection: number;
  modifiers: ModifierItem[];
}

interface MenuItemProps {
  item: {
    title: string;
    description: string;
    price: number;
    in_stock: boolean;
    food_type: string;
    image_url?: string;
    ref_id: string;
    modifier_groups: ModifierGroup[];
    taxes?: {
      ref_id: string;
      title: string;
      value: number;
    }[];
  };
  searchQuery?: string;
}

const MenuItem = ({ item, searchQuery = '' }: MenuItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const hasModifierGroups = item.modifier_groups?.length > 0;

  const variantGroups =
    item.modifier_groups?.filter(group => group.type === 'variant') || [];
  const addonGroups =
    item.modifier_groups?.filter(group => group.type === 'addon') || [];

  const { user } = useUser();

  const showInr =
    user?.tenantName === 'cure_food' || user?.tenantName === 'nik_bakers';

  // Function to highlight search terms
  const highlightSearchTerm = (text: string) => {
    if (!searchQuery) return text;

    const regex = new RegExp(`(${searchQuery})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{ backgroundColor: '#FFD700', fontWeight: 'bold' }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        mb: 2,
        backgroundColor: '#fff',
        overflow: 'hidden',
        ...(searchQuery &&
          (item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.description
              .toLowerCase()
              .includes(searchQuery.toLowerCase())) && {
            borderColor: '#FFD700',
            borderWidth: '2px',
            boxShadow: '0 0 8px rgba(255, 215, 0, 0.3)',
          }),
      }}
    >
      {/* Header section */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            borderBottom: isExpanded ? '1px solid' : 'none',
            borderColor: isExpanded ? 'divider' : 'transparent',
          }}
        >
          {/* Left section with image and content */}
          <Box sx={{ flex: 1, display: 'flex', gap: 2 }}>
            {/* Image section - only show if image_url exists and no error */}
            {item.image_url && !imageError && (
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: '#f5f5f5',
                  flexShrink: 0, // Prevent image from shrinking
                }}
              >
                <img
                  src={item.image_url}
                  alt={item.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  onError={() => setImageError(true)}
                />
              </Box>
            )}

            {/* Content section */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}
              >
                {item.food_type === '2' && (
                  <Box
                    component="span"
                    sx={{
                      width: 13,
                      height: 13,
                      borderRadius: 1,
                      border: '1px solid #E74C3C',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      padding: '4px',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '100%',
                        backgroundColor: '#E74C3C',
                      }}
                    />
                  </Box>
                )}
                {item.food_type === '1' && (
                  <Box
                    component="span"
                    sx={{
                      width: 13,
                      height: 13,
                      borderRadius: 1,
                      border: '1px solid #4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      padding: '4px',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '100%',
                        backgroundColor: '#4CAF50',
                      }}
                    />
                  </Box>
                )}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: 'custom.textBlue',
                  }}
                >
                  {highlightSearchTerm(item.title)}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 1,
                  color: 'lightGray.text',
                  fontSize: '12px',
                  maxWidth: '95%',
                }}
              >
                {highlightSearchTerm(item.description)}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: '1px solid',
                    borderColor: item.in_stock ? 'divider' : '#E74C3C',
                    minWidth: '78px',
                    width: 'fit-content',
                    borderRadius: '8px',
                    px: 1,
                    py: 0.5,
                    backgroundColor: !item.in_stock ? '#FFF0F0' : undefined,
                  }}
                >
                  <CheckCircle
                    size={16}
                    color={item.in_stock ? '#4CAF50' : '#E74C3C'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {item.in_stock ? 'In Stock' : 'Out of Stock'}
                  </Typography>
                </Box>

                {/* Combined modifier groups selection info */}
                {item.in_stock && hasModifierGroups && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {item.modifier_groups.map(group => (
                      <Box
                        key={group.ref_id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          minWidth: '78px',
                          width: 'fit-content',
                          borderRadius: '8px',
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {group.title}: {group.min_selection}-
                          {group.max_selection}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Right section with price and expand button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 500, fontSize: '1rem' }}
              >
                {item.price} {showInr ? 'Rs.' : '$'}
              </Typography>
              {item.taxes && item.taxes.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
                >
                  (+{item.taxes[0].value}% taxes)
                </Typography>
              )}
            </Box>

            {hasModifierGroups && (
              <IconButton
                size="small"
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease',
                }}
              >
                <CaretDown />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {/* Expanded content */}
      {hasModifierGroups && isExpanded && (
        <Box>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              overflow: 'hidden',
              my: 1,
              mx: 3,
            }}
          >
            {/* Variants section */}
            {variantGroups.length > 0 && (
              <Box>
                {/* Header row */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '200px 1fr',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#F8F9FA',
                  }}
                >
                  <Typography
                    sx={{
                      px: 2,
                      py: 1,
                      color: '#666',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    Variant
                  </Typography>
                  <Typography
                    sx={{
                      px: 2,
                      py: 1,
                      color: '#666',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    Options
                  </Typography>
                </Box>

                {/* Variant rows */}
                {variantGroups.map(group => (
                  <Box
                    key={group.ref_id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '200px 1fr',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        p: 2,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        color: '#000',
                        fontSize: '0.875rem',
                      }}
                    >
                      {group.title}
                    </Typography>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {group.modifiers.map(modifier => (
                          <Chip
                            key={modifier.ref_id}
                            label={`${modifier.title} ${modifier.price > 0 ? `(+${modifier.price}${showInr ? 'Rs.' : '$'}) ` : ''}`}
                            sx={{
                              backgroundColor: '#F5F5F5',
                              borderRadius: '4px',
                              height: '32px',
                              '&:hover': {
                                backgroundColor: '#EEEEEE',
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              overflow: 'hidden',
              my: 1,
              mx: 3,
            }}
          >
            {/* Add-ons section */}
            {addonGroups.length > 0 && (
              <Box>
                {/* Header row */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '200px 1fr',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#F8F9FA',
                  }}
                >
                  <Typography
                    sx={{
                      px: 2,
                      py: 1,
                      color: '#666',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    Add-Ons
                  </Typography>
                  <Typography
                    sx={{
                      px: 2,
                      py: 1,
                      color: '#666',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    Options
                  </Typography>
                </Box>

                {/* Add-on rows */}
                {addonGroups.map(group => (
                  <Box
                    key={group.ref_id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '200px 1fr',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        p: 2,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        color: '#000',
                        fontSize: '0.875rem',
                      }}
                    >
                      {group.title}
                    </Typography>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {group.modifiers.map(modifier => (
                          <Chip
                            key={modifier.ref_id}
                            label={`${modifier.title} ${modifier.price > 0 ? `(+${modifier.price}${showInr ? 'Rs.' : '$'}) ` : ''}`}
                            sx={{
                              backgroundColor: '#F5F5F5',
                              borderRadius: '4px',
                              height: '32px',
                              '&:hover': {
                                backgroundColor: '#EEEEEE',
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MenuItem;
